export const WIZARD_DRAFT_KEY = 'pagix-admin-template-wizard-v1';
export const LEGACY_WIZARD_DRAFT_KEY = 'landcraft-admin-template-wizard-v1';

const GOAL_RECOMMENDATIONS = {
  leads: { objective: 'Collect qualified demo requests', primaryCta: 'Request demo', pageType: 'organic-seo', variants: ['organic'], pageId: 'demo-landing', segment: 'demo' },
  waitlist: { objective: 'Build a qualified launch waitlist', primaryCta: 'Join waitlist', pageType: 'waitlist', variants: ['organic'], pageId: 'waitlist', segment: 'waitlist' },
  launch: { objective: 'Explain the new product clearly and drive early adoption', primaryCta: 'Get started', pageType: 'organic-seo', variants: ['organic'], pageId: 'product-launch', segment: 'product' },
  sales: { objective: 'Convert ready buyers into customers', primaryCta: 'Buy now', pageType: 'paid-lp', variants: ['paid'], pageId: 'sales-landing', segment: 'offer' },
  campaign: { objective: 'Convert campaign traffic with one focused message', primaryCta: 'Get started', pageType: 'paid-lp', variants: ['paid'], pageId: 'campaign-landing', segment: 'campaign' },
};

const INDUSTRY_AUDIENCES = {
  software: { b2b: 'Decision makers evaluating software for their teams', b2c: 'People looking for a simpler digital product' },
  hr: { b2b: 'HR and People Operations leaders at growing companies', b2c: 'Employees and managers looking for a clearer people experience' },
  ecommerce: { b2b: 'Commerce teams looking to improve conversion and operations', b2c: 'Online shoppers comparing practical purchase options' },
  finance: { b2b: 'Finance leaders who need secure and auditable workflows', b2c: 'People looking for a trustworthy financial service' },
  healthcare: { b2b: 'Healthcare teams that need reliable and compliant workflows', b2c: 'People comparing clear and trustworthy care options' },
  services: { b2b: 'Business buyers comparing specialist service providers', b2c: 'People looking for a credible specialist they can trust' },
  education: { b2b: 'Education leaders evaluating tools for staff and learners', b2c: 'Learners looking for a practical path to a better outcome' },
  realestate: { b2b: 'Property teams looking for a clearer operating workflow', b2c: 'People comparing property and real estate options' },
};

const INDUSTRY_NAMES = {
  software: 'Software and SaaS',
  hr: 'HR and People Operations',
  ecommerce: 'E-commerce and retail',
  finance: 'Finance and fintech',
  healthcare: 'Healthcare',
  services: 'Professional services',
  education: 'Education',
  realestate: 'Real estate',
};

function recommendationAudience(industry, businessModel) {
  const model = businessModel === 'b2c' ? 'b2c' : 'b2b';
  return INDUSTRY_AUDIENCES[industry]?.[model]
    || (model === 'b2c' ? 'People actively comparing solutions in this category' : 'Decision makers evaluating a solution for their organization');
}

export function applyIntakeRecommendations(draft) {
  const goal = GOAL_RECOMMENDATIONS[draft.journeyGoal] || GOAL_RECOMMENDATIONS.leads;
  const previous = draft.recommendationSnapshot || {};
  const audience = recommendationAudience(draft.industry, draft.businessModel);
  const tone = ['finance', 'healthcare'].includes(draft.industry)
    ? 'Clear, trustworthy and evidence-led'
    : draft.industry === 'ecommerce'
      ? 'Direct, useful and conversion-focused'
      : 'Clear, credible and concise';
  if (!draft.audience.trim() || draft.audience === previous.audience) draft.audience = audience;
  if (!draft.objective.trim() || draft.objective === previous.objective) draft.objective = goal.objective;
  if (!draft.primaryCta.trim() || draft.primaryCta === previous.primaryCta) draft.primaryCta = goal.primaryCta;
  if (!draft.tone.trim() || draft.tone === previous.tone || (!previous.tone && draft.tone === 'Clear, credible and concise')) draft.tone = tone;

  const firstPage = draft.pages[0] || newWizardPage();
  const previousPage = previous.page;
  const pageStillRecommended = previousPage
    && firstPage.pageId === previousPage.pageId
    && firstPage.segment === previousPage.segment
    && firstPage.pageType === previousPage.pageType
    && firstPage.variants.join(',') === previousPage.variants.join(',');
  if (!firstPage.hook.trim() || firstPage.hook === previous.objective) firstPage.hook = draft.objective;
  if (draft.pages.length === 1 && (['main-landing', ''].includes(firstPage.pageId) || pageStillRecommended)) {
    Object.assign(firstPage, {
      pageId: goal.pageId,
      segment: goal.segment,
      pageType: goal.pageType,
      variants: goal.variants,
      axis: draft.industry ? 'industry' : 'use-case',
    });
  }
  draft.pages = [firstPage, ...draft.pages.slice(1)];
  draft.recommendationSnapshot = {
    audience,
    objective: goal.objective,
    primaryCta: goal.primaryCta,
    tone,
    page: { pageId: goal.pageId, segment: goal.segment, pageType: goal.pageType, variants: goal.variants },
  };
  return draft;
}

export function countGeneratedContent(log) {
  return String(log || '').split('\n').filter((line) => line.trim().startsWith('✓ content/')).length;
}

export function newWizardPlan(index = 0) {
  return {
    id: index ? `plan-${index + 1}` : 'starter',
    name: index ? `Plan ${index + 1}` : 'Starter',
    billingLabel: 'Monthly subscription',
    price: '',
    priceDetail: '',
    description: '',
    ctaLabel: 'Get started',
    ctaHref: '#get-started',
  };
}

export function newWizardPage(index = 0) {
  return {
    pageId: index ? `landing-${index + 1}` : 'main-landing',
    axis: 'use-case',
    segment: index ? `landing-${index + 1}` : 'main',
    hook: '',
    tier: 'T1',
    pageType: 'organic-seo',
    variants: ['organic'],
  };
}

export function newWizardLogo() {
  return { name: '', src: '', href: '' };
}

export function newWizardTestimonial() {
  return { quote: '', author: '', role: '', company: '', result: '', sourceLabel: '', sourceUrl: '' };
}

export function newWizardStory() {
  return {
    kind: 'testimonial',
    quote: '', author: '', role: '',
    title: '', summary: '',
    company: '', companyLogoSrc: '', companyLogoAlt: '',
    mediaSrc: '', mediaAlt: '',
    metricValue: '', metricLabel: '',
    sourceLabel: '', sourceUrl: '',
  };
}

export function createWizardDraft() {
  return {
    version: 2,
    currentStep: 0,
    industry: '',
    industryOther: '',
    businessModel: '',
    projectStage: '',
    journeyGoal: '',
    recommendationSnapshot: null,
    brandId: '',
    domain: '',
    languages: ['en'],
    additionalLanguages: '',
    companyBrief: '',
    primaryCta: '',
    audience: '',
    offer: '',
    objective: '',
    differentiators: '',
    designReferences: '',
    notes: '',
    tone: 'Clear, credible and concise',
    primary: '#0f6cbd',
    secondary: '#242424',
    font: 'Segoe UI',
    waitlistEndpoint: '',
    pricing: {
      model: 'none',
      heading: '',
      intro: '',
      productName: '',
      features: '',
      plans: [newWizardPlan()],
      footnote: '',
    },
    proof: {
      enabled: false,
      relationship: 'customers',
      logoWallLabel: 'Trusted by teams at',
      logos: [newWizardLogo(), newWizardLogo()],
      testimonialHeading: 'What customers say',
      testimonials: [newWizardTestimonial()],
    },
    storyRail: {
      enabled: false,
      heading: 'What customers are saying',
      intro: '',
      background: '#111318',
      foreground: '#ffffff',
      accent: '#4f9cff',
      autoAdvanceMs: 6500,
      stories: [newWizardStory(), newWizardStory()],
    },
    pages: [newWizardPage()],
    result: null,
  };
}

export function loadWizardDraft(storage) {
  try {
    const raw = storage.getItem(WIZARD_DRAFT_KEY) ?? storage.getItem(LEGACY_WIZARD_DRAFT_KEY);
    const saved = JSON.parse(raw);
    if (!saved || ![1, 2].includes(saved.version)) return createWizardDraft();
    const base = createWizardDraft();
    // Drafts saved before the "Hikayeler" step existed have no storyRail key.
    // Their currentStep still uses the old numbering, where 5 meant "Kontrol"
    // (now "Hikayeler") — anything at or past that point must shift by one.
    const isPreStoryRailDraft = !saved.storyRail;
    const v1AdjustedStep = saved.version === 1 ? Number(saved.currentStep || 0) + 1 : Number(saved.currentStep || 0);
    const stepAfterStoryRailInsert = isPreStoryRailDraft && v1AdjustedStep >= 5 ? v1AdjustedStep + 1 : v1AdjustedStep;
    const migratedStep = Math.max(0, Math.min(7, stepAfterStoryRailInsert));
    const savedProof = saved.proof || {};
    const legacyTestimonial = savedProof.testimonial;
    const hasLegacyTestimonial = legacyTestimonial
      && Object.values(legacyTestimonial).some((value) => String(value || '').trim());
    const testimonials = Array.isArray(savedProof.testimonials)
      ? savedProof.testimonials
      : hasLegacyTestimonial ? [legacyTestimonial] : base.proof.testimonials;
    return {
      ...base,
      ...saved,
      version: 2,
      currentStep: migratedStep,
      pricing: { ...base.pricing, ...(saved.pricing || {}) },
      proof: { ...base.proof, ...savedProof, testimonials },
      storyRail: { ...base.storyRail, ...(saved.storyRail || {}) },
    };
  } catch {
    return createWizardDraft();
  }
}

export function saveWizardDraft(storage, draft) {
  storage.setItem(WIZARD_DRAFT_KEY, JSON.stringify(draft));
}

export function splitWizardLines(value) {
  return String(value || '').split('\n').map((item) => item.trim()).filter(Boolean);
}

function optional(value) {
  const normalized = String(value || '').trim();
  return normalized || undefined;
}

export function buildTemplateBundle(draft) {
  const languages = [...new Set(draft.languages)];
  const industry = (draft.industry === 'other' ? draft.industryOther : INDUSTRY_NAMES[draft.industry] || draft.industry).trim();
  const pages = draft.pages.map((page) => ({
    pageId: page.pageId.trim(),
    axis: page.axis.trim(),
    segment: page.segment.trim(),
    hook: page.hook.trim(),
    tier: page.tier,
    pageType: page.pageType,
    langs: languages,
    variants: page.variants,
  }));

  const pricing = draft.pricing.model === 'none' ? undefined : {
    model: draft.pricing.model,
    heading: draft.pricing.heading.trim(),
    intro: optional(draft.pricing.intro),
    productName: draft.pricing.productName.trim(),
    features: splitWizardLines(draft.pricing.features),
    plans: draft.pricing.plans.map((plan) => ({
      id: plan.id.trim(),
      name: plan.name.trim(),
      billingLabel: plan.billingLabel.trim(),
      price: plan.price.trim(),
      priceDetail: optional(plan.priceDetail),
      description: optional(plan.description),
      ctaLabel: plan.ctaLabel.trim(),
      ctaHref: plan.ctaHref.trim(),
    })),
    footnote: optional(draft.pricing.footnote),
  };

  const completeLogos = draft.proof.logos
    .filter((logo) => logo.name.trim() || logo.src.trim() || logo.href.trim())
    .map((logo) => ({ name: logo.name.trim(), src: logo.src.trim(), ...(optional(logo.href) ? { href: logo.href.trim() } : {}) }));
  const testimonialRequiredFields = ['quote', 'author', 'role', 'company', 'sourceLabel', 'sourceUrl'];
  const completeTestimonials = draft.proof.testimonials
    .filter((testimonial) => testimonialRequiredFields.some((field) => String(testimonial[field] || '').trim()))
    .map((testimonial) => ({
      quote: testimonial.quote.trim(),
      author: testimonial.author.trim(),
      role: testimonial.role.trim(),
      company: testimonial.company.trim(),
      ...(optional(testimonial.result) ? { result: testimonial.result.trim() } : {}),
      sourceLabel: testimonial.sourceLabel.trim(),
      sourceUrl: testimonial.sourceUrl.trim(),
    }));
  const socialProof = !draft.proof.enabled ? undefined : {
    ...(completeLogos.length ? {
      logoWall: {
        relationship: draft.proof.relationship,
        label: draft.proof.logoWallLabel.trim(),
        logos: completeLogos,
      },
    } : {}),
    ...(completeTestimonials.length ? {
      testimonialHeading: draft.proof.testimonialHeading.trim(),
      testimonials: completeTestimonials,
    } : {}),
  };

  const storyRailFields = ['quote', 'author', 'role', 'title', 'summary', 'company', 'mediaSrc', 'sourceLabel', 'sourceUrl'];
  const completeStories = draft.storyRail.stories
    .filter((story) => storyRailFields.some((field) => String(story[field] || '').trim()))
    .map((story) => {
      const media = optional(story.mediaSrc)
        ? { src: story.mediaSrc.trim(), alt: (story.mediaAlt || story.company || story.title).trim() }
        : undefined;
      const shared = {
        company: story.company.trim(),
        ...(optional(story.companyLogoSrc) ? {
          companyLogo: { src: story.companyLogoSrc.trim(), alt: (story.companyLogoAlt || story.company).trim() },
        } : {}),
        ...(media ? { media } : {}),
        ...(optional(story.metricValue) && optional(story.metricLabel) ? {
          metric: { value: story.metricValue.trim(), label: story.metricLabel.trim() },
        } : {}),
        sourceLabel: story.sourceLabel.trim(),
        sourceUrl: story.sourceUrl.trim(),
      };
      return story.kind === 'product-proof'
        ? { kind: 'product-proof', title: story.title.trim(), summary: story.summary.trim(), ...shared }
        : { kind: 'testimonial', quote: story.quote.trim(), author: story.author.trim(), role: story.role.trim(), ...shared };
    });
  const storyRail = !draft.storyRail.enabled ? undefined : {
    heading: draft.storyRail.heading.trim(),
    ...(optional(draft.storyRail.intro) ? { intro: draft.storyRail.intro.trim() } : {}),
    appearance: {
      background: draft.storyRail.background.trim(),
      foreground: draft.storyRail.foreground.trim(),
      accent: draft.storyRail.accent.trim(),
    },
    autoAdvanceMs: Number.isFinite(Number(draft.storyRail.autoAdvanceMs)) ? Number(draft.storyRail.autoAdvanceMs) : 6500,
    stories: completeStories,
  };

  const brand = {
    id: draft.brandId.trim(),
    domain: draft.domain.trim(),
    languages,
    primaryCta: draft.primaryCta.trim(),
    tone: draft.tone.trim(),
    theme: { primary: draft.primary.trim(), secondary: draft.secondary.trim(), font: draft.font.trim() },
    analytics: { ga4Id: null, googleAdsId: null, linkedInPartnerId: null },
    waitlistEndpoint: draft.waitlistEndpoint.trim(),
    deployTarget: { protocol: 'sftp', host: '', path: '' },
    activeTiers: [...new Set(pages.map((page) => page.tier))],
    generationBrief: {
      ...(industry ? { industry } : {}),
      ...(draft.businessModel ? { businessModel: draft.businessModel } : {}),
      ...(draft.projectStage ? { projectStage: draft.projectStage } : {}),
      ...(draft.journeyGoal ? { journeyGoal: draft.journeyGoal } : {}),
      audience: draft.audience.trim(),
      offer: draft.offer.trim(),
      objective: draft.objective.trim(),
      differentiators: splitWizardLines(draft.differentiators),
      designReferences: splitWizardLines(draft.designReferences),
      ...(optional(draft.notes) ? { notes: draft.notes.trim() } : {}),
    },
    ...(pricing ? { pricing } : {}),
    ...(socialProof ? { socialProof } : {}),
    ...(storyRail ? { storyRail } : {}),
  };

  return { brand, rows: pages };
}
